import { Menu, Info, Share2, Shield, Sun, Moon, Monitor, Download, Lock, FileText, Scale } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useTheme } from "next-themes";
import { usePWAInstall } from "@/hooks/usePWAInstall";

interface MobileMenuProps {
  isAdmin: boolean;
  currentPath: string;
  onNavigate: (path: string) => void;
  onShare: () => void;
}

export const MobileMenu = ({
  isAdmin,
  currentPath,
  onNavigate,
  onShare,
}: MobileMenuProps) => {
  const { theme, setTheme } = useTheme();
  const { canShowButton, isInstallable, promptInstall } = usePWAInstall();
  const isActive = (path: string) => currentPath === path;

  const handleInstall = async () => {
    if (isInstallable) {
      await promptInstall();
    } else {
      onNavigate('/install');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" aria-label="Menu">
          <Menu className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-56 bg-background z-50">
        {/* Theme section */}
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          Thème
        </DropdownMenuLabel>
        <DropdownMenuItem 
          onClick={() => setTheme('light')}
          className={theme === 'light' ? 'bg-accent' : ''}
        >
          <Sun className="h-4 w-4 mr-2" />
          <span>Clair</span>
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => setTheme('dark')}
          className={theme === 'dark' ? 'bg-accent' : ''}
        >
          <Moon className="h-4 w-4 mr-2" />
          <span>Sombre</span>
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => setTheme('system')}
          className={theme === 'system' ? 'bg-accent' : ''}
        >
          <Monitor className="h-4 w-4 mr-2" />
          <span>Système</span>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Navigation section */}
        <DropdownMenuItem 
          onClick={() => onNavigate('/about')}
          className={isActive('/about') ? 'bg-accent' : ''}
        >
          <Info className="h-4 w-4 mr-2" />
          <span>À propos</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => onNavigate('/confidentialite')}
          className={isActive('/confidentialite') ? 'bg-accent' : ''}
        >
          <Lock className="h-4 w-4 mr-2" />
          <span>Confidentialité</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => onNavigate('/cgu')}
          className={isActive('/cgu') ? 'bg-accent' : ''}
        >
          <FileText className="h-4 w-4 mr-2" />
          <span>CGU</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => onNavigate('/mentions-legales')}
          className={isActive('/mentions-legales') ? 'bg-accent' : ''}
        >
          <Scale className="h-4 w-4 mr-2" />
          <span>Mentions légales</span>
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={onShare}>
          <Share2 className="h-4 w-4 mr-2" />
          <span>Partager</span>
        </DropdownMenuItem>
        
        {canShowButton && (
          <DropdownMenuItem onClick={handleInstall}>
            <Download className="h-4 w-4 mr-2" />
            <span>Installer l'app</span>
          </DropdownMenuItem>
        )}
        
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => onNavigate('/admin')}
              className={isActive('/admin') ? 'bg-accent' : ''}
            >
              <Shield className="h-4 w-4 mr-2" />
              <span>Admin</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
